import type { RefObject } from 'react';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

import { N0, B100 } from '@atlaskit/theme/colors';
import ImageIcon from '@atlaskit/icon/core/migration/image';
import { token } from '@atlaskit/tokens';
import DropdownMenu from '@atlaskit/dropdown-menu';
import { layers } from '@atlaskit/theme/constants';

import type { UnsplashResult } from '@confluence/custom-sites-extensions/entry-points/UnsplashSearch';

import type { COVERPICTUREWIDTH } from './contentTopperConstants';
import { UnsplashPopupMenuContent } from './UnsplashPopupMenuContent';

export type UnsplashDropdownMenuProps = {
	imageSrc?: string;
	imageAltText?: string;
	initialUnsplashInfoArray: UnsplashResult[] | undefined;
	closePopup: () => void;
	openMediaBrowser?: () => void;
	coverPictureState: any;
	setIsUploadingPicture: (isUploadingPicture: boolean) => void;
	handleUnsplashUpload: (url: string) => void;
	floatingToolbarButtonStyle?: object;
	userOffline?: boolean;
	isModalOpenState: boolean;
	onDelete?: () => void;
	onReposition?: () => void;
	dropdownRef?: RefObject<HTMLElement | null>;
	dropdownPopupComponentRef?: RefObject<HTMLElement | null>;
	contentId: string;
	onImageWidthChange?: (coverPictureWidth: COVERPICTUREWIDTH) => void;
	onCoverImageWidthDropdownOptionSelected?: (isCoverImageWidthChanged: boolean) => void;
};

// eslint-disable-next-line check-i18n-id-prefix/i18n-id-prefix-with-package-name-defineMessages
const i18n = defineMessages({
	headerLabel: {
		id: 'custom-sites-extensions.add.header.image.message.floating-toolbar',
		description:
			'Message displayed on the button which allows the user to change the header image in a page in the floating toolbar.',
		defaultMessage: 'Header image',
	},
	updateImage: {
		id: 'custom-sites-extensions.page-title.popup.update.header.image',
		description: 'Message user can click on to update/change their header image.',
		defaultMessage: 'Update header image',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownTrigger = styled.button<{ userOffline?: boolean }>({
	display: 'flex',
	flexFlow: 'column nowrap',
	alignItems: 'center',
	color: token('color.background.input', N0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: ({ userOffline }) => (userOffline ? 'not-allowed' : 'pointer'),
	background: 'none',
	border: 'none',
	outlineColor: token('color.border.information', B100),
});

export const UnsplashDropdownMenu = ({
	isModalOpenState,
	closePopup,
	initialUnsplashInfoArray,
	openMediaBrowser,
	userOffline,
	handleUnsplashUpload,
	setIsUploadingPicture,
	coverPictureState,
	floatingToolbarButtonStyle,
	onReposition,
	onDelete,
	imageSrc,
	dropdownRef,
	dropdownPopupComponentRef,
	contentId,
	onImageWidthChange,
	onCoverImageWidthDropdownOptionSelected,
}: UnsplashDropdownMenuProps) => {
	return (
		<DropdownMenu
			defaultOpen={false}
			isOpen={isModalOpenState}
			placement="bottom"
			zIndex={layers.dialog()}
			strategy="absolute"
			shouldRenderToParent
			trigger={({ triggerRef, ...props }) => (
				<DropdownTrigger
					data-testid="update-image-button"
					{...props}
					tabIndex={0}
					aria-expanded={isModalOpenState}
					ref={triggerRef as React.RefObject<HTMLButtonElement>}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={floatingToolbarButtonStyle || {}}
					userOffline={userOffline}
					disabled={userOffline}
				>
					<ImageIcon LEGACY_size="large" label="" color={token('color.icon.inverse', 'white')} />
					<FormattedMessage {...i18n.updateImage} />
				</DropdownTrigger>
			)}
		>
			<UnsplashPopupMenuContent
				dropdownRef={dropdownRef}
				contentId={contentId}
				imageSrc={imageSrc}
				initialUnsplashInfoArray={initialUnsplashInfoArray}
				openMediaBrowser={openMediaBrowser}
				handleUnsplashUpload={handleUnsplashUpload}
				setIsUploadingPicture={setIsUploadingPicture}
				coverPictureState={coverPictureState}
				closePopup={closePopup}
				onReposition={onReposition}
				onDelete={onDelete}
				placement="bottom"
				userOffline={userOffline}
				dropdownPopupComponentRef={dropdownPopupComponentRef}
				onImageWidthChange={onImageWidthChange}
				isPageTitleComponent
				onCoverImageWidthDropdownOptionSelected={onCoverImageWidthDropdownOptionSelected}
			/>
		</DropdownMenu>
	);
};
